import { useCallback, useMemo, useState } from 'react';
import { useSession } from '@/features/auth';
import {
  GLOBAL_SEARCH_RECENTS_KEY_PREFIX,
  GLOBAL_SEARCH_RECENTS_MAX,
  type GlobalSearchRecentHit,
  type GlobalSearchRecentStored,
} from '../domain/global-search.types';

function storageKey(clinicId: string): string {
  return `${GLOBAL_SEARCH_RECENTS_KEY_PREFIX}${clinicId}`;
}

function readRecents(clinicId: string): GlobalSearchRecentStored[] {
  try {
    const raw = localStorage.getItem(storageKey(clinicId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is GlobalSearchRecentStored => {
        if (typeof item !== 'object' || item === null) return false;
        const row = item as Record<string, unknown>;
        return (
          typeof row['id'] === 'string' &&
          (row['kind'] === 'patient' ||
            row['kind'] === 'appointment' ||
            row['kind'] === 'invoice') &&
          typeof row['label'] === 'string' &&
          typeof row['href'] === 'string' &&
          typeof row['at'] === 'number'
        );
      })
      .slice(0, GLOBAL_SEARCH_RECENTS_MAX);
  } catch {
    return [];
  }
}

function writeRecents(clinicId: string, items: GlobalSearchRecentStored[]): void {
  localStorage.setItem(storageKey(clinicId), JSON.stringify(items.slice(0, GLOBAL_SEARCH_RECENTS_MAX)));
}

function toHit(item: GlobalSearchRecentStored): GlobalSearchRecentHit {
  return {
    kind: 'recent',
    id: `recent-${item.kind}-${item.id}`,
    entityId: item.id,
    entityKind: item.kind,
    label: item.label,
    href: item.href,
    at: item.at,
  };
}

export interface UseGlobalSearchRecentsResult {
  recents: GlobalSearchRecentHit[];
  pushRecent: (entry: Omit<GlobalSearchRecentStored, 'at'>) => void;
  clearRecents: () => void;
}

/** Local-only recent search destinations, scoped per active clinic. */
export function useGlobalSearchRecents(): UseGlobalSearchRecentsResult {
  const { activeClinic } = useSession();
  const clinicId = activeClinic?.clinicId ?? 'unknown';
  const [version, setVersion] = useState(0);

  const stored = useMemo(() => {
    void version;
    return readRecents(clinicId);
  }, [clinicId, version]);

  const recents = useMemo(() => stored.map(toHit), [stored]);

  const pushRecent = useCallback(
    (entry: Omit<GlobalSearchRecentStored, 'at'>) => {
      if (!activeClinic?.clinicId) return;
      const next: GlobalSearchRecentStored[] = [
        { ...entry, at: Date.now() },
        ...readRecents(clinicId).filter((r) => r.href !== entry.href),
      ].slice(0, GLOBAL_SEARCH_RECENTS_MAX);
      writeRecents(clinicId, next);
      setVersion((v) => v + 1);
    },
    [activeClinic?.clinicId, clinicId],
  );

  const clearRecents = useCallback(() => {
    if (!activeClinic?.clinicId) return;
    localStorage.removeItem(storageKey(clinicId));
    setVersion((v) => v + 1);
  }, [activeClinic?.clinicId, clinicId]);

  return { recents, pushRecent, clearRecents };
}
